import { Modal } from "../../utils/Modal";
import { Form } from "../../core/Form";
import { initAdminFormControls } from "../global/initAdminFormControls";

/**
 * CMS 공용 form-fragment 모달 (메뉴 항목·섹션·요소 공용).
 * [data-cms-modal] (data-url, data-title) 클릭 → fragment 로드 → app-modal 주입 →
 * initAdminFormControls → new Form 제출 → 성공 시 reload. 폼 id = cms-modal-form.
 */
function escHtml(s: string): string {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
}

let open = false;

async function openModal(url: string, title: string): Promise<void> {
    if (open) return;
    open = true;

    let html: string;
    try {
        const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!res.ok) { open = false; await Modal.alert("폼을 불러오지 못했습니다."); return; }
        html = await res.text();
    } catch {
        open = false;
        await Modal.alert("네트워크 오류가 발생했습니다.");
        return;
    }

    const root = document.getElementById("app-modal-root") ?? (() => {
        const el = document.createElement("div");
        el.id = "app-modal-root";
        document.body.appendChild(el);
        return el;
    })();

    const backdrop = document.createElement("div");
    backdrop.className = "app-modal-backdrop";
    const box = document.createElement("div");
    box.className = "app-modal-box app-modal-box--wide cms-item-modal";
    box.innerHTML = `
        <div class="app-modal-header">
            <h2 class="app-modal-title">${escHtml(title)}</h2>
            <button type="button" class="app-modal-close" data-cms-modal-close aria-label="닫기"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
        </div>
        <div class="app-modal-body app-modal-body--scroll cms-item-modal__body" data-lenis-prevent></div>
        <div class="app-modal-footer">
            <button type="button" class="app-modal-btn" data-cms-modal-close>취소</button>
            <button type="submit" form="cms-modal-form" class="app-modal-btn app-modal-btn--primary">저장</button>
        </div>
    `;
    const body = box.querySelector<HTMLElement>(".cms-item-modal__body")!;
    body.innerHTML = html;

    const fresh = body.querySelector<HTMLInputElement>('input[name="_token"]')?.value;
    if (fresh) {
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.setAttribute("content", fresh);
        document.querySelectorAll<HTMLInputElement>('input[name="_token"]').forEach((i) => { i.value = fresh; });
    }

    backdrop.appendChild(box);
    root.appendChild(backdrop);
    initAdminFormControls(body);

    const close = (): void => { backdrop.remove(); open = false; };
    box.querySelectorAll("[data-cms-modal-close]").forEach((b) => b.addEventListener("click", close));
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    box.addEventListener("click", (e) => e.stopPropagation());

    const form = body.querySelector<HTMLFormElement>("form[data-cms-modal-form]");
    if (form) {
        new Form(form, {
            onSuccess() { close(); window.location.reload(); },
            onFail(msg) { if (msg) void Modal.alert(msg); },
            requestFailedMessage: "요청에 실패했습니다.",
            invalidResponseMessage: "응답을 처리할 수 없습니다.",
        }).init();
    }

    const del = body.querySelector<HTMLButtonElement>("[data-cms-modal-delete]");
    if (del) {
        del.addEventListener("click", async () => {
            if (!(await Modal.confirm(del.dataset.confirm ?? "삭제할까요?"))) return;
            const action = del.dataset.action ?? "";
            if (!action) return;
            const csrf =
                document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
                body.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
            try {
                const res = await fetch(action, {
                    method: "POST",
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                        "X-CSRF-TOKEN": csrf,
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: `_method=DELETE&_token=${encodeURIComponent(csrf)}`,
                });
                if (res.ok) { close(); window.location.reload(); }
                else { await Modal.alert("삭제에 실패했습니다."); }
            } catch { await Modal.alert("네트워크 오류가 발생했습니다."); }
        });
    }
}

interface BoundWindow extends Window { _cmsFormModalBound?: boolean; }

export function initCmsFormModal(): void {
    const w = window as BoundWindow;
    if (w._cmsFormModalBound) return;
    w._cmsFormModalBound = true;
    document.addEventListener("click", (e) => {
        const t = (e.target as HTMLElement).closest<HTMLElement>("[data-cms-modal]");
        if (!t) return;
        const url = t.dataset.url ?? "";
        if (!url) return;
        e.preventDefault();
        void openModal(url, t.dataset.title ?? "");
    });
}
